import { Check } from "@phosphor-icons/react"
import { Box } from "@tokenterminal/ui/Box"
import { LinkButtonV4 } from "@tokenterminal/ui/Button/LinkButton.v4"
import { Toggle } from "@tokenterminal/ui/Input"
import { Row } from "@tokenterminal/ui/Row"
import { Stack } from "@tokenterminal/ui/Stack"
import { Text } from "@tokenterminal/ui/Text"
import { useState } from "react"
import {
  pricingCard,
  pricingCardFeature,
  pricingCardWrapper,
} from "./PricingCards.css"
import { BASE_PATH } from "@/constants/app.mjs"

const CheckIcon = () => (
  <Check size={14} weight="bold" color="hsl(165, 89.40%, 63.10%)" />
)

const FeatureLine = ({
  active = true,
  name,
}: {
  active?: Boolean
  name: String
}) => {
  if (active) {
    return (
      <Box className={pricingCardFeature}>
        <CheckIcon /> <Text size="14">{name}</Text>
      </Box>
    )
  } else {
    return (
      <Box
        className={pricingCardFeature}
        paddingLeft="7x"
        style={{ textDecoration: "line-through", opacity: 0.5 }}
      >
        <Text size="14">{name}</Text>
      </Box>
    )
  }
}

const pricingPlans: Array<{
  name: string
  monthlyPrice: number
  annualPrice: number
  description: string
  features: Array<{ name: string; active: boolean }>
  cta: JSX.Element
}> = [
  {
    name: "Free",
    monthlyPrice: 0,
    annualPrice: 0,
    description: "For hobbyists and emerging investors",
    features: [
      {
        name: "Full Historical Data",
        active: true,
      },
      {
        name: "3 Dashboards",
        active: true,
      },
      {
        name: "Sheets and Excel Plugin",
        active: true,
      },
      {
        name: "CSV Download Data",
        active: false,
      },

      {
        name: "Invite Team Members",
        active: false,
      },
    ],
    cta: (
      <LinkButtonV4
        width={"100%"}
        href={`${BASE_PATH}/explorer/auth/signup`}
        variant="outline"
      >
        Sign up for Free
      </LinkButtonV4>
    ),
  },
  {
    name: "Pro",
    monthlyPrice: 350,
    annualPrice: 292,
    description: "For professional investors and teams",
    features: [
      {
        name: "Full Historical Data",
        active: true,
      },
      {
        name: "Unlimited Dashboards",
        active: true,
      },
      {
        name: "Sheets and Excel Plugin",
        active: true,
      },
      {
        name: "CSV Download Data",
        active: true,
      },
      {
        name: "Invite Team Members",
        active: true,
      },
    ],
    cta: (
      <LinkButtonV4
        color="success"
        width={"100%"}
        href={`${BASE_PATH}/explorer/auth/signup`}
      >
        Start 7 day trial
      </LinkButtonV4>
    ),
  },
  {
    name: "API",
    monthlyPrice: 999,
    annualPrice: 834,
    description: "For developers and builders",
    features: [
      {
        name: "Everything in Pro",
        active: true,
      },
      {
        name: "REST APIs for metrics and projects",
        active: true,
      },
      {
        name: "SDKs in multiple languages",
        active: true,
      },
      {
        name: "250,000 requests/day",
        active: true,
      },
      {
        name: "Status Page for API Uptime",
        active: true,
      },
    ],
    cta: (
      <LinkButtonV4
        width={"100%"}
        variant="outline"
        href={`${BASE_PATH}/explorer/auth/signup?returnTo=upgrade-to-api`}
      >
        Sign up for API
      </LinkButtonV4>
    ),
  },
  {
    name: "Data Room",
    monthlyPrice: 4999,
    annualPrice: 3999,
    description: "For institutional teams and platforms.",
    features: [
      {
        name: "Raw blockchain data access",
        active: true,
      },
      {
        name: "Data sharing in Big Query and Snowflake",
        active: true,
      },
      {
        name: "Dedicated support & SLA",
        active: true,
      },
    ],
    cta: (
      <LinkButtonV4 variant="outline" href="/contact">
        Contact Sales
      </LinkButtonV4>
    ),
  },
]

const PricingTable = () => {
  const [showAnnual, setShowAnnual] = useState(false)

  const toggleAnnual = () => {
    setShowAnnual(!showAnnual)
  }

  return (
    <Stack className={pricingCardWrapper}>
      <Row justifyContent="center" alignItems="center" gap="4x">
        <Row as="label" name="annual">
          <Text>Monthly</Text>
          <Toggle name={"annual"} id={"annual"} onChange={toggleAnnual} />
          <Text>Annual</Text>
          <Box
            style={{
              border: "1px solid #383838",
              borderRadius: "16px",
              fontSize: "12px",
              padding: "3px 6px 3px 7px",
              color: "#bbb",
            }}
          >
            Save 15%
          </Box>
        </Row>
      </Row>
      <Row justifyContent="center" gap="4x" flexDirection={["column", "row"]}>
        {pricingPlans.slice(0, 3).map((plan) => (
          <Stack className={pricingCard}>
            <Row>
              <Text size="20" fontWeight="500">
                {plan.name}
              </Text>
              {showAnnual && plan.name !== "Free" ? (
                <Text size="20">
                  {`$${plan.annualPrice}`}{" "}
                  <Text
                    as="span"
                    size="20"
                    color="muted"
                    style={{ textDecoration: "line-through" }}
                  >
                    {`$${plan.monthlyPrice}`}
                  </Text>
                </Text>
              ) : (
                <Text size="20" color="muted">{`$${plan.monthlyPrice}`}</Text>
              )}
            </Row>
            <Text color="muted" marginBottom="4x">
              {plan.description}
            </Text>
            <Stack gap="3x" marginBottom="4x">
              {plan.features.map((feature) => (
                <FeatureLine name={feature.name} active={feature.active} />
              ))}
            </Stack>
            <Row marginTop="1x">{plan.cta}</Row>
          </Stack>
        ))}
      </Row>
      <Row>
        {pricingPlans.slice(3, 4).map((plan) => (
          <Stack className={pricingCard} style={{ background: "transparent" }}>
            <Row>
              <Text size="20" fontWeight="500">
                {plan.name}
              </Text>
              <Text size="16" color="muted">{`Custom pricing`}</Text>
            </Row>
            <Text color="muted" marginBottom="4x">
              {plan.description}
            </Text>
            <Row
              start
              gap={["4x", "12x"]}
              marginBottom="4x"
              flexDirection={["column", "row"]}
              alignItems={["flexStart", "center"]}
            >
              {plan.features.map((feature) => (
                <FeatureLine name={feature.name} active={feature.active} />
              ))}
            </Row>
            <Row>{plan.cta}</Row>
          </Stack>
        ))}
      </Row>
    </Stack>
  )
}

export default PricingTable
